import React, { useState } from 'react';
import { QuoteData } from '../../App';
import { FileTextIcon, UsersIcon, SettingsIcon, DollarSignIcon, CalendarIcon, TruckIcon, ShieldIcon, ChevronDownIcon, PlusCircleIcon, TrashIcon, PercentIcon, TagIcon, CheckCircleIcon, MapPinIcon, RefreshCwIcon } from 'lucide-react';
import { CurrencyChangeModal } from '../CurrencyChangeModal';
interface PartsSelectionProps {
  quoteData: QuoteData;
  setQuoteData: (data: QuoteData) => void;
}
interface Discount {
  id: string;
  name: string;
  percentage: number;
}
export function PartsSelection({
  quoteData,
  setQuoteData
}: PartsSelectionProps) {
  const [offerType, setOfferType] = useState<'dealer' | 'dealer-customer' | 'customer'>('dealer');
  const [selectedDealer, setSelectedDealer] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [baseDiscount, setBaseDiscount] = useState(10);
  const [additionalDiscounts, setAdditionalDiscounts] = useState<Discount[]>([]);
  const [probability, setProbability] = useState(75);
  const [validUntil, setValidUntil] = useState(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [priceList, setPriceList] = useState('spain-eur');
  const [dealerCurrency, setDealerCurrency] = useState('USD');
  const [customerCurrency, setCustomerCurrency] = useState('USD');
  // Modal states
  const [isDealerCurrencyModalOpen, setIsDealerCurrencyModalOpen] = useState(false);
  const [isCustomerCurrencyModalOpen, setIsCustomerCurrencyModalOpen] = useState(false);
  const [isSingleCurrencyModalOpen, setIsSingleCurrencyModalOpen] = useState(false);
  const priceListOptions = [{
    id: 'spain-eur',
    name: 'Spain Price List (EUR)',
    currency: 'EUR',
    warehouse: 'AISco - Albacete, Spain',
    region: 'Aisco Europe'
  }, {
    id: 'riyadh-r2r4r5-usd',
    name: 'Riyadh Price List R2R4R5 (USD)',
    currency: 'USD',
    warehouse: 'Alkhorayef Manufacture - Riyadh',
    region: 'Aisco América'
  }, {
    id: 'riyadh-r3-usd',
    name: 'Riyadh Price List R3 (USD)',
    currency: 'USD',
    warehouse: 'Alkhorayef Manufacture - Riyadh',
    region: 'Aisco Europe'
  }, {
    id: 'us-usd',
    name: 'US Price List (USD)',
    currency: 'USD',
    warehouse: 'Pierce - San Antonio, Texas, USA',
    region: 'United States'
  }];
  const getSelectedPriceList = () => {
    return priceListOptions.find(pl => pl.id === priceList);
  };
  const handleAddDiscount = () => {
    const newDiscount: Discount = {
      id: Date.now().toString(),
      name: 'Additional Discount',
      percentage: 0
    };
    setAdditionalDiscounts([...additionalDiscounts, newDiscount]);
  };
  const handleRemoveDiscount = (id: string) => {
    setAdditionalDiscounts(additionalDiscounts.filter(d => d.id !== id));
  };
  const handleDiscountChange = (id: string, field: 'name' | 'percentage', value: string | number) => {
    setAdditionalDiscounts(additionalDiscounts.map(d => d.id === id ? {
      ...d,
      [field]: value
    } : d));
  };
  const getTotalDiscount = () => {
    const additional = additionalDiscounts.reduce((sum, d) => sum + d.percentage, 0);
    return baseDiscount + additional;
  };
  const handleDealerCurrencyChange = (config: {
    currency: string;
    exchangeRatio: number;
    safetyMargin: number;
  }) => {
    setDealerCurrency(config.currency);
    // Aquí puedes guardar también el exchangeRatio y safetyMargin si los necesitas
    console.log('Dealer currency config:', config);
  };
  const handleCustomerCurrencyChange = (config: {
    currency: string;
    exchangeRatio: number;
    safetyMargin: number;
  }) => {
    setCustomerCurrency(config.currency);
    // Aquí puedes guardar también el exchangeRatio y safetyMargin si los necesitas
    console.log('Customer currency config:', config);
  };
  const handleSingleCurrencyChange = (config: {
    currency: string;
    exchangeRatio: number;
    safetyMargin: number;
  }) => {
    setQuoteData({
      ...quoteData,
      currency: config.currency
    });
    // Aquí puedes guardar también el exchangeRatio y safetyMargin si los necesitas
    console.log('Single currency config:', config);
  };
  return <div className="w-full space-y-6" data-id="element-863">
      {/* Quote Overview Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-864">
        <div className="bg-gray-50 p-5 border-b border-gray-200" data-id="element-865">
          <div className="flex items-center gap-3" data-id="element-866">
            <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center" data-id="element-867">
              <FileTextIcon className="w-5 h-5 text-white" data-id="element-868" />
            </div>
            <div data-id="element-869">
              <h3 className="text-base font-semibold text-gray-900" data-id="element-870">
                Quote Overview
              </h3>
              <p className="text-sm text-gray-500" data-id="element-871">
                Basic information about this quotation
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6" data-id="element-872">
          <div data-id="element-873">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-874">
              Quote Name <span className="text-red-500" data-id="element-875">*</span>
            </label>
            <input type="text" value={quoteData.name} onChange={e => setQuoteData({
            ...quoteData,
            name: e.target.value
          })} placeholder="Enter a descriptive name for this quote" className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 placeholder-gray-400" data-id="element-876" />
          </div>
          <div data-id="element-877">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-878">
              Win Probability
            </label>
            <div className="flex items-center gap-4" data-id="element-879">
              <input type="range" min="0" max="100" value={probability} onChange={e => setProbability(Number(e.target.value))} className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" style={{
              background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${probability}%, rgb(229, 231, 235) ${probability}%, rgb(229, 231, 235) 100%)`
            }} data-id="element-880" />
              <div className="flex items-center gap-2 min-w-[80px]" data-id="element-881">
                <input type="number" min="0" max="100" value={probability} onChange={e => setProbability(Number(e.target.value))} className="w-16 px-2 py-2 bg-white border-2 border-gray-300 rounded-lg text-center font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-882" />
                <span className="text-gray-600 font-medium" data-id="element-883">%</span>
              </div>
            </div>
            <div className="mt-2 flex justify-between text-xs text-gray-500" data-id="element-884">
              <span data-id="element-885">Low</span>
              <span data-id="element-886">Medium</span>
              <span data-id="element-887">High</span>
            </div>
          </div>
        </div>
      </div>
      {/* Dealer & Customer Details Section - REDESIGNED */}

      {/* Quote Conditions Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-888">
        <div className="bg-gray-50 p-5 border-b border-gray-200" data-id="element-889">
          <div className="flex items-center gap-3" data-id="element-890">
            <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center" data-id="element-891">
              <SettingsIcon className="w-5 h-5 text-white" data-id="element-892" />
            </div>
            <div data-id="element-893">
              <h3 className="text-base font-semibold text-gray-900" data-id="element-894">
                Quote Conditions
              </h3>
              <p className="text-sm text-gray-500" data-id="element-895">
                Define validity and payment terms
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6" data-id="element-896">
          <div data-id="element-897">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-898">
              Payment Terms
            </label>
            <textarea value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} placeholder="Enter payment terms and conditions..." rows={3} className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none text-gray-900 placeholder-gray-400" data-id="element-899" />
          </div>
          <div data-id="element-900">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-901">
              Quote Valid Until <span className="text-red-500" data-id="element-902">*</span>
            </label>
            <div className="relative" data-id="element-903">
              <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-904" />
              <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-905" />
            </div>
            <p className="mt-2 text-xs text-gray-500" data-id="element-906">
              Default: 30 days from today
            </p>
          </div>
        </div>
      </div>
      {/* Financial Details Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-907">
        <div className="bg-gray-50 p-5 border-b border-gray-200" data-id="element-908">
          <div className="flex items-center gap-3" data-id="element-909">
            <div className="w-10 h-10 bg-gray-400 rounded-lg flex items-center justify-center" data-id="element-910">
              <DollarSignIcon className="w-5 h-5 text-white" data-id="element-911" />
            </div>
            <div data-id="element-912">
              <h3 className="text-base font-semibold text-gray-900" data-id="element-913">
                Financial Details
              </h3>
              <p className="text-sm text-gray-500" data-id="element-914">
                Configure price list and currency settings
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6" data-id="element-915">
          {/* Price List Selection */}
          <div data-id="element-916">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-917">
              Price List <span className="text-red-500" data-id="element-918">*</span>
            </label>
            <div className="relative" data-id="element-919">
              <select value={priceList} onChange={e => {
              setPriceList(e.target.value);
              const selected = priceListOptions.find(pl => pl.id === e.target.value);
              if (selected && offerType !== 'dealer-customer') {
                setQuoteData({
                  ...quoteData,
                  currency: selected.currency
                });
              }
            }} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-920">
                {priceListOptions.map(option => <option key={option.id} value={option.id} data-id="element-921">
                    {option.name}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-922" />
            </div>
            {getSelectedPriceList() && <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-923">
                <div className="mb-3 pb-2 border-b border-blue-200" data-id="element-924">
                  <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide" data-id="element-925">
                    Selling From
                  </p>
                  <p className="text-xs text-blue-700 mt-1" data-id="element-926">
                    This price list is configured to sell from the following
                    warehouse
                  </p>
                </div>
                <div className="space-y-2 text-sm" data-id="element-927">
                  <div className="flex items-start gap-2" data-id="element-928">
                    <MapPinIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-929" />
                    <div data-id="element-930">
                      <p className="font-semibold text-blue-900" data-id="element-931">
                        Origin Warehouse
                      </p>
                      <p className="text-blue-700" data-id="element-932">
                        {getSelectedPriceList()?.warehouse}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-933">
                    <TagIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-934" />
                    <div data-id="element-935">
                      <p className="font-semibold text-blue-900" data-id="element-936">
                        Sales Region
                      </p>
                      <p className="text-blue-700" data-id="element-937">
                        {getSelectedPriceList()?.region}
                      </p>
                    </div>
                  </div>
                  
                </div>
              </div>}
          </div>
          {/* Currency Configuration */}
          {offerType === 'dealer-customer' ? <div className="space-y-4" data-id="element-938">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200" data-id="element-939">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-940">
                  <DollarSignIcon className="w-4 h-4 text-blue-600" data-id="element-941" />
                  Dual Currency Configuration
                </h4>
                <p className="text-xs text-gray-600 mb-4" data-id="element-942">
                  Configure different currencies for dealer and customer quotes
                </p>
                <div className="grid grid-cols-2 gap-4" data-id="element-943">
                  {/* Dealer Currency */}
                  <div data-id="element-944">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-945">
                      Dealer Currency <span className="text-red-500" data-id="element-946">*</span>
                    </label>
                    <div className="flex gap-2" data-id="element-947">
                      <div className="flex-1 relative" data-id="element-948">
                        <select value={dealerCurrency} onChange={e => setDealerCurrency(e.target.value)} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-949">
                          <option value="USD" data-id="element-950">USD</option>
                          <option value="EUR" data-id="element-951">EUR</option>
                          <option value="GBP" data-id="element-952">GBP</option>
                          <option value="MXN" data-id="element-953">MXN</option>
                          <option value="SAR" data-id="element-954">SAR</option>
                        </select>
                        <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-955" />
                      </div>
                      <button onClick={() => setIsDealerCurrencyModalOpen(true)} className="px-4 py-3 text-blue-700 bg-white border-2 border-blue-300 rounded-lg hover:bg-blue-50 transition-all font-semibold flex items-center gap-2" data-id="element-956">
                        <RefreshCwIcon className="w-4 h-4" data-id="element-957" />
                        Change
                      </button>
                    </div>
                  </div>
                  {/* Customer Currency */}
                  <div data-id="element-958">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-959">
                      Customer Currency <span className="text-red-500" data-id="element-960">*</span>
                    </label>
                    <div className="flex gap-2" data-id="element-961">
                      <div className="flex-1 relative" data-id="element-962">
                        <select value={customerCurrency} onChange={e => setCustomerCurrency(e.target.value)} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-963">
                          <option value="USD" data-id="element-964">USD</option>
                          <option value="EUR" data-id="element-965">EUR</option>
                          <option value="GBP" data-id="element-966">GBP</option>
                          <option value="MXN" data-id="element-967">MXN</option>
                          <option value="SAR" data-id="element-968">SAR</option>
                        </select>
                        <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-969" />
                      </div>
                      <button onClick={() => setIsCustomerCurrencyModalOpen(true)} className="px-4 py-3 text-indigo-700 bg-white border-2 border-indigo-300 rounded-lg hover:bg-indigo-50 transition-all font-semibold flex items-center gap-2" data-id="element-970">
                        <RefreshCwIcon className="w-4 h-4" data-id="element-971" />
                        Change
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div> : <div data-id="element-972">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-973">
                Currency <span className="text-red-500" data-id="element-974">*</span>
              </label>
              <div className="flex gap-2" data-id="element-975">
                <div className="flex-1 relative" data-id="element-976">
                  <select value={quoteData.currency} onChange={e => setQuoteData({
                ...quoteData,
                currency: e.target.value
              })} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-977">
                    <option value="USD" data-id="element-978">USD - US Dollar</option>
                    <option value="EUR" data-id="element-979">EUR - Euro</option>
                    <option value="GBP" data-id="element-980">GBP - British Pound</option>
                    <option value="MXN" data-id="element-981">MXN - Mexican Peso</option>
                    <option value="SAR" data-id="element-982">SAR - Saudi Riyal</option>
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-983" />
                </div>
                <button onClick={() => setIsSingleCurrencyModalOpen(true)} className="px-4 py-3 text-blue-700 bg-white border-2 border-blue-300 rounded-lg hover:bg-blue-50 transition-all font-semibold flex items-center gap-2" data-id="element-984">
                  <RefreshCwIcon className="w-4 h-4" data-id="element-985" />
                  Change
                </button>
              </div>
            </div>}
        </div>
      </div>
      {/* Summary Card */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-6" data-id="element-986">
        <div className="flex items-start justify-between" data-id="element-987">
          <div data-id="element-988">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2" data-id="element-989">
              Information Summary
            </h3>
            <div className="space-y-1 text-sm text-gray-600" data-id="element-990">
              <p data-id="element-991">
                <span className="font-semibold text-gray-900" data-id="element-992">Quote Name:</span>{' '}
                {quoteData.name || 'Not set'}
              </p>
              <p data-id="element-993">
                <span className="font-semibold text-gray-900" data-id="element-994">
                  Probability:
                </span>{' '}
                {probability}%
              </p>
              <p data-id="element-995">
                <span className="font-semibold text-gray-900" data-id="element-996">Quote Type:</span>{' '}
                {offerType === 'dealer' && 'Dealer Quote'}
                {offerType === 'dealer-customer' && 'Dealer & Customer'}
                {offerType === 'customer' && 'Final Customer'}
              </p>
              {(offerType === 'dealer' || offerType === 'dealer-customer') && <p data-id="element-997">
                  <span className="font-semibold text-gray-900" data-id="element-998">
                    Total Discount:
                  </span>{' '}
                  {getTotalDiscount()}%
                </p>}
              <p data-id="element-999">
                <span className="font-semibold text-gray-900" data-id="element-1000">Currency:</span>{' '}
                {quoteData.currency}
              </p>
            </div>
          </div>
          <div className="text-right" data-id="element-1001">
            <p className="text-xs text-gray-500 mb-1" data-id="element-1002">Additional Costs</p>
            <p className="text-lg font-bold text-gray-900" data-id="element-1003">
              {quoteData.currency} $
              {(quoteData.transport + quoteData.insurance).toFixed(2)}
            </p>
          </div>
        </div>
      </div>
      {/* Currency Change Modals */}
      <CurrencyChangeModal isOpen={isDealerCurrencyModalOpen} onClose={() => setIsDealerCurrencyModalOpen(false)} currentCurrency={dealerCurrency} onApply={handleDealerCurrencyChange} title="Change Dealer Currency" data-id="element-1004" />
      <CurrencyChangeModal isOpen={isCustomerCurrencyModalOpen} onClose={() => setIsCustomerCurrencyModalOpen(false)} currentCurrency={customerCurrency} onApply={handleCustomerCurrencyChange} title="Change Customer Currency" data-id="element-1005" />
      <CurrencyChangeModal isOpen={isSingleCurrencyModalOpen} onClose={() => setIsSingleCurrencyModalOpen(false)} currentCurrency={quoteData.currency} onApply={handleSingleCurrencyChange} title="Change Currency" data-id="element-1006" />
    </div>;
}